import React from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { ArcadeFieldLabel, FieldLabel, PaddingInset, SsnInput as CdsSsnInput } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { TextInputElementProps } from '../../util/types'
import { FieldController } from '../FieldController/FieldController'
import { FormMaskedInput } from '../FormMaskedInput'

type SsnInputProps = {
  variant: 'FULL' | 'LAST_FOUR'
  name?: string
  isRequired?: boolean
} & TextInputElementProps

const SecurityText = styled.span`
  ${({ theme }) => theme.typography.metaText};
  color: ${props => props.theme.colors.disabledLabel};
`

export function SsnInput({ variant, name, defaultValue, validate, isRequired, ...props }: SsnInputProps) {
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (isArcadeMigrationEnabled) {
    const ssnConfig = {
      FULL: {
        label: t('SSN', { context: 'label for form field asking for a social security number' }),
        placeholder: t('9-digit SSN', {
          context: 'placeholder text for a form field asking for a social security number',
        }),
        requiredMessage: t('Enter your SSN.', {
          context: 'error message for form field when missing a social security number',
        }),
        lengthValidation: {
          value: 9,
          message: t('Enter a valid SSN.', {
            context: 'error message for a form field with an invalid social security number',
          }),
        },
      },
      LAST_FOUR: {
        label: t('Last 4 digits of SSN', {
          context: 'label for form field asking for last 4 digits of a social security number',
        }),
        placeholder: undefined,
        requiredMessage: t('Enter the last 4 digits of your SSN.', {
          context: 'error message for form field when missing the last 4 digits of a social security number',
        }),
        lengthValidation: {
          value: 4,
          message: t('Enter the last 4 of your SSN.', {
            context:
              'error message for form field when the last 4 digits of a social security number are the incorrect number of digits',
          }),
        },
      },
    } as const

    return (
      <ArcadeFieldLabel label={ssnConfig[variant].label}>
        <FieldController
          FieldComponent={CdsSsnInput}
          componentProps={{
            variant: variant,
            placeholder: ssnConfig[variant].placeholder,
            helpText: t('Secured with 256-bit encryption', {
              context: 'social security number form field description information',
            }),
            hideSsnAriaLabel: t('Hide SSN', {
              context: 'aria label for button to hide social security number in form field',
            }),
            showSsnAriaLabel: t('Show SSN', {
              context: 'aria label for button to show social security number in form field',
            }),
            ...props,
          }}
          defaultValue={defaultValue}
          name={name || 'ssn'}
          rules={{
            required: {
              value: !!isRequired,
              message: ssnConfig[variant].requiredMessage,
            },
            minLength: ssnConfig[variant].lengthValidation,
            maxLength: ssnConfig[variant].lengthValidation,
            validate,
          }}
        />
      </ArcadeFieldLabel>
    )
  }

  if (variant === 'LAST_FOUR') {
    return <LastFourSsnInput name={name} isRequired={isRequired} />
  }

  return <FullSsnInput name={name} isRequired={isRequired} />
}

function FullSsnInput({ name, isRequired }: Omit<SsnInputProps, 'variant'>) {
  const { t } = useTranslation()

  // Full SSNs have 9 digits.
  const lengthValidation = {
    value: 9,
    message: t('Enter a valid SSN.', {
      context: 'error message for a form field with an invalid social security number',
    }),
  }

  return (
    <FieldLabel label={t('SSN', { context: 'label for form field asking for a social security number' })}>
      <FormMaskedInput
        name={name || 'ssn'}
        format="###-##-####"
        placeholder={t('9-digit SSN', {
          context: 'placeholder text for a form field asking for a social security number',
        })}
        rules={{
          required: {
            value: !!isRequired,
            message: t('Enter your SSN.', {
              context: 'error message for form field when missing a social security number',
            }),
          },
          minLength: lengthValidation,
          maxLength: lengthValidation,
        }}
      />
      <PaddingInset>
        <SecurityText>
          {t('Secured with 256-bit encryption', {
            context: 'social security number form field description information',
          })}
        </SecurityText>
      </PaddingInset>
    </FieldLabel>
  )
}

function LastFourSsnInput({ name, isRequired }: Omit<SsnInputProps, 'variant'>) {
  const { t } = useTranslation()

  const lengthValidation = {
    value: 4,
    message: t('Enter the last 4 of your SSN.', {
      context:
        'error message for form field when the last 4 digits of a social security number are the incorrect number of digits',
    }),
  }

  return (
    <FieldLabel
      label={t('Last 4 digits of SSN', {
        context: 'label for form field asking for last 4 digits of a social security number',
      })}
    >
      <FormMaskedInput
        name={name || 'ssn'}
        format="####"
        placeholder={t('1234', {
          context: 'placeholder for form field asking for the last 4 digits of a social security number',
        })}
        rules={{
          required: {
            value: !!isRequired,
            message: t('Enter the last 4 digits of your SSN.', {
              context: 'error message for form field when missing the last 4 digits of a social security number',
            }),
          },
          minLength: lengthValidation,
          maxLength: lengthValidation,
        }}
      />
      <PaddingInset>
        <SecurityText>
          {t('Secured with 256-bit encryption', {
            context: 'social security number form field description information',
          })}
        </SecurityText>
      </PaddingInset>
    </FieldLabel>
  )
}
